export interface ServiceData {
  slug: string;
  name: string;
  fullName: string;
  badge?: string;
  icon: string;
  heroTitle: string;
  heroSubtitle: string;
  price: string;
  priceDetail: string;
  metaTitle: string;
  metaDesc: string;
  heroImage: string;
  whatIs: string;
  whatIsDetail: string;
  obligations: string;
  obligationItems: string[];
  steps: { title: string; desc: string; icon: string }[];
  checklist: string[];
  prices: { label: string; price: string; highlight?: boolean }[];
  faq: { q: string; a: string }[];
  related: string[];
  errors: string[];
}

export const servicesData: Record<string, ServiceData> = {
  dpe: {
    slug: "dpe",
    name: "DPE",
    fullName: "Diagnostic de Performance Énergétique",
    badge: "★ Service phare",
    icon: "ri-fire-line",
    heroTitle: "DPE à Paris & Île-de-France",
    heroSubtitle: "Diagnostic de Performance Énergétique certifié COFRAC — Rapport le jour même, dès 130 €",
    price: "Dès 130 €",
    priceDetail: "TTC, déplacement inclus en IDF",
    metaTitle: "DPE Paris & Île-de-France — Diagnostic de Performance Énergétique | Up Diag",
    metaDesc: "DPE certifié COFRAC à Paris et en Île-de-France. Rapport le jour même, dès 130 €. Obligatoire pour vendre ou louer. RDV sous 48h.",
    heroImage: "/img/readdy/dpe-hero.jpg",
    whatIs: "Le Diagnostic de Performance Énergétique (DPE) évalue la consommation d'énergie d'un logement et son impact sur les émissions de gaz à effet de serre. Il attribue deux étiquettes : une étiquette énergie (de A à G) et une étiquette climat.",
    whatIsDetail: "Depuis la réforme de juillet 2021, le DPE est devenu opposable juridiquement. Il est obligatoire pour toute vente ou mise en location d'un bien immobilier. Sa validité est de 10 ans. À Paris et en Île-de-France, le DPE est un critère déterminant pour la valeur verte de votre bien.",
    obligations: "Le DPE est obligatoire dans les cas suivants :",
    obligationItems: [
      "Vente d'un bien immobilier (appartement, maison, local)",
      "Mise en location d'un logement",
      "Construction neuve (DPE neuf)",
      "Affichage obligatoire dans les annonces immobilières",
      "Logements classés F ou G : travaux de rénovation imposés dès 2025",
    ],
    steps: [
      { title: "Prise de rendez-vous", desc: "Devis en ligne en 2 min ou par téléphone. Intervention sous 48h dans toute l'IDF.", icon: "ri-calendar-check-line" },
      { title: "Visite du bien", desc: "Notre diagnostiqueur certifié COFRAC inspecte votre logement : isolation, chauffage, ventilation, menuiseries.", icon: "ri-search-eye-line" },
      { title: "Collecte des données", desc: "Relevé des équipements, mesures thermiques, analyse des systèmes de chauffage et de production d'eau chaude.", icon: "ri-clipboard-line" },
      { title: "Calcul & étiquetage", desc: "Calcul de la consommation énergétique selon la méthode 3CL-DPE 2021. Attribution des étiquettes A à G.", icon: "ri-bar-chart-line" },
      { title: "Rapport certifié", desc: "Rapport complet envoyé par email le jour même. Enregistrement sur le registre national ADEME.", icon: "ri-file-check-line" },
    ],
    checklist: [
      "Titre de propriété ou bail",
      "Factures de chauffage et d'électricité (si disponibles)",
      "Plans du logement (si disponibles)",
      "Accès à tous les espaces (cave, combles, chaufferie)",
      "Informations sur les travaux réalisés",
    ],
    prices: [
      { label: "Studio / T1 (< 35 m²)", price: "130 €", highlight: true },
      { label: "T2 / T3 (35–80 m²)", price: "150 €", highlight: true },
      { label: "T4+ / Maison (> 80 m²)", price: "180 €", highlight: true },
      { label: "Local commercial", price: "Sur devis", highlight: false },
    ],
    faq: [
      { q: "Quelle est la durée de validité d'un DPE ?", a: "Le DPE a une validité de 10 ans depuis la réforme de juillet 2021. Les DPE réalisés avant 2018 ne sont plus valides depuis le 1er janvier 2023." },
      { q: "Le DPE est-il obligatoire pour une location ?", a: "Oui, le DPE est obligatoire pour toute mise en location. Il doit être annexé au bail et mentionné dans l'annonce immobilière." },
      { q: "Que se passe-t-il si mon logement est classé F ou G ?", a: "Les logements classés G sont interdits à la location depuis le 1er janvier 2025. Les logements F seront interdits à partir de 2028. Des travaux de rénovation énergétique sont nécessaires." },
      { q: "Combien de temps dure la visite pour un DPE ?", a: "La visite dure entre 1h et 2h selon la surface et la complexité du bien. Vous recevez votre rapport le jour même par email." },
      { q: "Le DPE est-il opposable ?", a: "Oui, depuis juillet 2021, le DPE est opposable juridiquement. En cas d'erreur significative, l'acheteur ou le locataire peut se retourner contre le vendeur ou le bailleur." },
    ],
    related: ["pack-vente", "pack-location", "electricite"],
    errors: [
      "Ne pas fournir les factures d'énergie disponibles",
      "Bloquer l'accès à certaines pièces ou équipements",
      "Confondre DPE vierge et DPE opposable",
      "Utiliser un DPE expiré dans une annonce immobilière",
    ],
  },

  "pack-vente": {
    slug: "pack-vente",
    name: "Pack Vente",
    fullName: "Dossier de Diagnostic Technique (DDT) complet",
    icon: "ri-home-2-line",
    heroTitle: "Pack Vente — DDT complet à Paris & IDF",
    heroSubtitle: "Tous vos diagnostics obligatoires pour la vente en un seul rendez-vous — Dès 280 €",
    price: "Dès 280 €",
    priceDetail: "TTC, tous diagnostics inclus, déplacement inclus en IDF",
    metaTitle: "Pack Vente Diagnostic Immobilier Paris — DDT Complet | Up Diag",
    metaDesc: "Pack vente complet à Paris et en IDF : DPE, Amiante, Plomb, Électricité, Gaz, Loi Carrez en un seul RDV. Certifié COFRAC, dès 280 €.",
    heroImage: "/img/readdy/pv-hero.jpg",
    whatIs: "Le Dossier de Diagnostic Technique (DDT) regroupe l'ensemble des diagnostics immobiliers obligatoires pour la vente d'un bien. Notre Pack Vente vous permet de les réaliser tous en un seul rendez-vous avec un diagnostiqueur certifié.",
    whatIsDetail: "Le DDT doit être annexé à la promesse de vente ou à l'acte authentique. Il protège l'acheteur en l'informant sur l'état du bien et dégage la responsabilité du vendeur en cas de vices cachés liés aux éléments diagnostiqués.",
    obligations: "Le DDT est obligatoire pour toute vente immobilière. Il comprend selon le bien :",
    obligationItems: [
      "DPE (Diagnostic de Performance Énergétique) — tous biens",
      "Diagnostic Amiante — biens construits avant juillet 1997",
      "CREP Plomb — biens construits avant 1949",
      "Diagnostic Électricité — installations de plus de 15 ans",
      "Diagnostic Gaz — installations de plus de 15 ans",
      "Mesurage Loi Carrez — lots en copropriété",
      "État des Risques et Pollutions (ERP) — tous biens",
    ],
    steps: [
      { title: "Devis personnalisé", desc: "Nous analysons votre bien (année, surface, type) pour établir la liste exacte des diagnostics nécessaires.", icon: "ri-file-list-3-line" },
      { title: "Rendez-vous unique", desc: "Un seul déplacement de notre diagnostiqueur pour réaliser l'ensemble des diagnostics en 2h à 3h.", icon: "ri-calendar-check-line" },
      { title: "Inspection complète", desc: "Visite méthodique de toutes les pièces, équipements et installations selon les normes en vigueur.", icon: "ri-search-eye-line" },
      { title: "Rapports certifiés", desc: "Tous vos rapports certifiés COFRAC envoyés par email le jour même, prêts pour votre notaire.", icon: "ri-file-check-line" },
    ],
    checklist: [
      "Titre de propriété",
      "Plans du logement (si disponibles)",
      "Accès à toutes les pièces, cave, combles",
      "Accès au tableau électrique et aux compteurs",
      "Factures d'énergie récentes",
      "Règlement de copropriété (si applicable)",
    ],
    prices: [
      { label: "Appartement jusqu'à 50 m²", price: "280 €", highlight: true },
      { label: "Appartement 50–100 m²", price: "320 €", highlight: true },
      { label: "Maison jusqu'à 150 m²", price: "380 €", highlight: true },
      { label: "Maison > 150 m²", price: "Sur devis", highlight: false },
    ],
    faq: [
      { q: "Combien de temps dure la visite pour un pack vente ?", a: "La visite dure entre 2h et 3h pour un appartement standard. Pour une maison, comptez 3h à 4h. Vous recevez tous vos rapports le jour même." },
      { q: "Quels diagnostics sont inclus dans le pack vente ?", a: "Le pack vente inclut : DPE, Amiante (si avant 1997), Plomb/CREP (si avant 1949), Électricité (si +15 ans), Gaz (si +15 ans), Loi Carrez (si copropriété). Nous adaptons le pack à votre bien." },
      { q: "Les rapports sont-ils acceptés par les notaires ?", a: "Oui, tous nos rapports sont certifiés COFRAC et acceptés par l'ensemble des notaires et agences immobilières en France." },
      { q: "Puis-je commander les diagnostics séparément ?", a: "Oui, vous pouvez commander chaque diagnostic individuellement. Cependant, le pack vente est plus économique et pratique car tout est réalisé en un seul rendez-vous." },
    ],
    related: ["dpe", "amiante", "plomb"],
    errors: [
      "Oublier un diagnostic obligatoire selon l'année de construction",
      "Utiliser des diagnostics expirés lors de la signature",
      "Ne pas fournir le DDT complet au notaire avant la promesse",
      "Confondre les diagnostics vente et location",
    ],
  },

  "pack-location": {
    slug: "pack-location",
    name: "Pack Location",
    fullName: "Diagnostics obligatoires pour la location",
    icon: "ri-key-2-line",
    heroTitle: "Pack Location — Diagnostics obligatoires à Paris & IDF",
    heroSubtitle: "Tous les diagnostics requis pour louer votre bien en toute conformité — Dès 180 €",
    price: "Dès 180 €",
    priceDetail: "TTC, déplacement inclus en IDF",
    metaTitle: "Pack Location Diagnostic Immobilier Paris & IDF — Dès 180 € | Up Diag",
    metaDesc: "Pack location à Paris et en IDF : DPE, Plomb, Électricité, Gaz en un seul RDV. Certifié COFRAC, dès 180 €. Obligatoire pour louer.",
    heroImage: "/img/readdy/pl-hero.jpg",
    whatIs: "Le Pack Location regroupe l'ensemble des diagnostics immobiliers obligatoires pour mettre un bien en location. Ces diagnostics doivent être annexés au contrat de bail et mentionnés dans l'annonce de location.",
    whatIsDetail: "Depuis la loi ALUR, les obligations diagnostics pour la location se sont renforcées. Un bailleur qui ne fournit pas les diagnostics requis s'expose à des sanctions et ne peut pas augmenter le loyer en cours de bail.",
    obligations: "Les diagnostics obligatoires pour la location sont :",
    obligationItems: [
      "DPE — obligatoire pour tous les logements",
      "CREP Plomb — logements construits avant 1949",
      "Diagnostic Électricité — installations de plus de 15 ans",
      "Diagnostic Gaz — installations de plus de 15 ans",
      "État des Risques et Pollutions (ERP) — tous logements",
      "Diagnostic Bruit — zones exposées au bruit aérien",
    ],
    steps: [
      { title: "Devis en ligne", desc: "Indiquez l'adresse, la surface et l'année de construction. Devis immédiat adapté à votre bien.", icon: "ri-file-list-3-line" },
      { title: "Intervention sous 48h", desc: "Notre diagnostiqueur certifié intervient à votre adresse dans toute l'Île-de-France.", icon: "ri-calendar-check-line" },
      { title: "Diagnostics complets", desc: "Réalisation de tous les diagnostics requis en une seule visite de 1h30 à 2h.", icon: "ri-search-eye-line" },
      { title: "Rapports le jour même", desc: "Tous vos rapports certifiés envoyés par email le jour même, prêts à annexer au bail.", icon: "ri-file-check-line" },
    ],
    checklist: [
      "Titre de propriété ou mandat de gestion",
      "Accès à toutes les pièces du logement",
      "Accès au tableau électrique",
      "Accès aux équipements de chauffage et eau chaude",
      "Informations sur l'année de construction",
    ],
    prices: [
      { label: "Studio / T1 (< 35 m²)", price: "180 €", highlight: true },
      { label: "T2 / T3 (35–80 m²)", price: "220 €", highlight: true },
      { label: "T4+ / Maison (> 80 m²)", price: "260 €", highlight: true },
      { label: "Parc locatif (5 biens+)", price: "Sur devis", highlight: false },
    ],
    faq: [
      { q: "Quelle est la durée de validité des diagnostics location ?", a: "Le DPE est valable 10 ans. Le diagnostic plomb est valable 6 ans (1 an si présence de plomb). Les diagnostics électricité et gaz sont valables 6 ans pour la location." },
      { q: "Puis-je louer un logement classé G ?", a: "Non. Depuis le 1er janvier 2025, les logements classés G (consommation > 450 kWh/m²/an) sont interdits à la location. Les logements F seront interdits à partir de 2028." },
      { q: "Que risque-t-on sans les diagnostics ?", a: "Sans les diagnostics obligatoires, le bailleur ne peut pas augmenter le loyer en cours de bail et s'expose à des poursuites judiciaires du locataire." },
      { q: "Les diagnostics sont-ils les mêmes pour une location meublée ?", a: "Oui, les mêmes diagnostics sont obligatoires pour la location meublée et la location nue." },
    ],
    related: ["dpe", "plomb", "electricite"],
    errors: [
      "Louer sans DPE valide (interdit depuis 2023)",
      "Oublier le diagnostic plomb pour les biens avant 1949",
      "Ne pas renouveler les diagnostics expirés avant la relocation",
      "Louer un logement classé G depuis janvier 2025",
    ],
  },

  amiante: {
    slug: "amiante",
    name: "Amiante",
    fullName: "Diagnostic Amiante",
    icon: "ri-shield-check-line",
    heroTitle: "Diagnostic Amiante à Paris & Île-de-France",
    heroSubtitle: "Obligatoire pour les biens construits avant juillet 1997 — Certifié COFRAC, dès 120 €",
    price: "Dès 120 €",
    priceDetail: "TTC, déplacement inclus en IDF",
    metaTitle: "Diagnostic Amiante Paris & IDF — Certifié COFRAC, Dès 120 € | Up Diag",
    metaDesc: "Diagnostic amiante certifié COFRAC à Paris et en Île-de-France. Obligatoire avant 1997. Rapport le jour même, dès 120 €. RDV sous 48h.",
    heroImage: "/img/readdy/am-hero.jpg",
    whatIs: "Le diagnostic amiante consiste à rechercher la présence de matériaux ou produits contenant de l'amiante dans les parties privatives et communes d'un immeuble bâti. L'amiante est une substance cancérigène interdite en France depuis 1997.",
    whatIsDetail: "Le diagnostic amiante est obligatoire pour tous les biens dont le permis de construire a été délivré avant le 1er juillet 1997. Il doit être réalisé par un diagnostiqueur certifié. En cas de présence d'amiante, des travaux de désamiantage peuvent être imposés.",
    obligations: "Le diagnostic amiante est obligatoire dans les cas suivants :",
    obligationItems: [
      "Vente d'un bien construit avant le 1er juillet 1997",
      "Travaux de rénovation ou démolition (DTA)",
      "Parties communes des immeubles collectifs",
      "Locaux professionnels et commerciaux avant 1997",
      "Renouvellement si présence d'amiante détectée",
    ],
    steps: [
      { title: "Prise de rendez-vous", desc: "Devis gratuit en ligne. Intervention sous 48h dans toute l'Île-de-France.", icon: "ri-calendar-check-line" },
      { title: "Inspection visuelle", desc: "Examen minutieux de tous les matériaux susceptibles de contenir de l'amiante : faux plafonds, dalles de sol, calorifugeages, etc.", icon: "ri-search-eye-line" },
      { title: "Prélèvements si nécessaire", desc: "En cas de doute, des prélèvements sont effectués et envoyés en laboratoire accrédité COFRAC pour analyse.", icon: "ri-test-tube-line" },
      { title: "Rapport certifié", desc: "Rapport complet avec localisation des matériaux amiantés, état de conservation et préconisations. Envoi le jour même.", icon: "ri-file-check-line" },
    ],
    checklist: [
      "Permis de construire ou date de construction du bien",
      "Accès à toutes les pièces, combles, cave, sous-sol",
      "Accès aux gaines techniques et locaux techniques",
      "Plans du bâtiment si disponibles",
      "Historique des travaux réalisés",
    ],
    prices: [
      { label: "Appartement (toute surface)", price: "120 €", highlight: true },
      { label: "Maison individuelle", price: "150 €", highlight: true },
      { label: "Local commercial / professionnel", price: "Sur devis", highlight: false },
      { label: "Parties communes immeuble", price: "Sur devis", highlight: false },
    ],
    faq: [
      { q: "Mon bien a été construit en 1985, ai-je besoin d'un diagnostic amiante ?", a: "Oui, absolument. Tout bien dont le permis de construire a été délivré avant le 1er juillet 1997 est concerné par le diagnostic amiante obligatoire." },
      { q: "Que se passe-t-il si de l'amiante est détectée ?", a: "Si de l'amiante est détectée en bon état de conservation, une surveillance périodique est recommandée. Si l'amiante est dégradée, des travaux de désamiantage sont obligatoires." },
      { q: "Quelle est la durée de validité du diagnostic amiante ?", a: "Si aucune amiante n'est détectée, le diagnostic a une durée illimitée. Si de l'amiante est présente, un contrôle périodique tous les 3 ans est obligatoire." },
      { q: "Le diagnostic amiante est-il obligatoire pour la location ?", a: "Pour la location, le diagnostic amiante des parties privatives n'est pas obligatoire à annexer au bail, mais le Dossier Technique Amiante (DTA) doit être tenu à disposition des occupants." },
    ],
    related: ["pack-vente", "plomb", "dpe"],
    errors: [
      "Croire que seuls les biens anciens sont concernés (avant 1997 = obligatoire)",
      "Ne pas faire analyser les matériaux douteux en laboratoire",
      "Ignorer les parties communes lors d'une vente en copropriété",
      "Réaliser des travaux sans diagnostic amiante préalable",
    ],
  },

  plomb: {
    slug: "plomb",
    name: "Plomb (CREP)",
    fullName: "Constat de Risque d'Exposition au Plomb",
    icon: "ri-test-tube-line",
    heroTitle: "Diagnostic Plomb (CREP) à Paris & IDF",
    heroSubtitle: "Obligatoire pour les biens construits avant 1949 — Certifié COFRAC, dès 110 €",
    price: "Dès 110 €",
    priceDetail: "TTC, déplacement inclus en IDF",
    metaTitle: "Diagnostic Plomb CREP Paris & IDF — Certifié COFRAC, Dès 110 € | Up Diag",
    metaDesc: "Diagnostic plomb CREP certifié COFRAC à Paris et en IDF. Obligatoire avant 1949. Résultats immédiats par fluorescence X, dès 110 €.",
    heroImage: "/img/readdy/pb-hero.jpg",
    whatIs: "Le Constat de Risque d'Exposition au Plomb (CREP) recherche la présence de plomb dans les revêtements (peintures, enduits) d'un logement. Le plomb est toxique, notamment pour les enfants en bas âge (saturnisme).",
    whatIsDetail: "Le CREP est obligatoire pour les logements construits avant le 1er janvier 1949, aussi bien pour la vente que pour la location. À Paris, une grande partie du parc immobilier haussmannien est concernée. La mesure est réalisée par fluorescence X, une technique non destructive et immédiate.",
    obligations: "Le CREP est obligatoire dans les cas suivants :",
    obligationItems: [
      "Vente d'un logement construit avant le 1er janvier 1949",
      "Location d'un logement construit avant le 1er janvier 1949",
      "Travaux dans un logement avant 1949",
      "Parties communes des immeubles avant 1949",
    ],
    steps: [
      { title: "Prise de rendez-vous", desc: "Devis gratuit, intervention sous 48h dans toute l'IDF.", icon: "ri-calendar-check-line" },
      { title: "Mesure par fluorescence X", desc: "Notre diagnostiqueur utilise un appareil à fluorescence X pour mesurer la teneur en plomb de chaque revêtement, pièce par pièce.", icon: "ri-scan-line" },
      { title: "Analyse des résultats", desc: "Identification des zones à risque selon les seuils réglementaires (1 mg/cm²). Évaluation de l'état de conservation.", icon: "ri-bar-chart-line" },
      { title: "Rapport CREP certifié", desc: "Rapport détaillé avec cartographie des zones à risque. Envoi par email le jour même.", icon: "ri-file-check-line" },
    ],
    checklist: [
      "Accès à toutes les pièces du logement",
      "Accès aux parties communes si copropriété",
      "Informations sur l'année de construction",
      "Historique des travaux de peinture réalisés",
    ],
    prices: [
      { label: "Appartement jusqu'à 50 m²", price: "110 €", highlight: true },
      { label: "Appartement 50–100 m²", price: "130 €", highlight: true },
      { label: "Maison individuelle", price: "150 €", highlight: true },
      { label: "Parties communes", price: "Sur devis", highlight: false },
    ],
    faq: [
      { q: "Mon appartement a été rénové, ai-je quand même besoin du CREP ?", a: "Oui. Si votre bien a été construit avant 1949, le CREP est obligatoire même après rénovation. Des traces de plomb peuvent subsister sous les nouvelles couches de peinture." },
      { q: "Quelle est la durée de validité du CREP ?", a: "Pour la vente : illimitée si absence de plomb, 1 an si présence de plomb. Pour la location : 6 ans si absence de plomb, 6 ans si présence de plomb (mais surveillance recommandée)." },
      { q: "Que faire si du plomb est détecté ?", a: "Si la concentration dépasse le seuil réglementaire et que les revêtements sont dégradés, des travaux de suppression ou de confinement du plomb sont obligatoires." },
      { q: "La mesure par fluorescence X est-elle destructive ?", a: "Non, la fluorescence X est une technique non destructive. Elle ne nécessite aucun prélèvement ni dégradation des revêtements." },
    ],
    related: ["pack-vente", "pack-location", "amiante"],
    errors: [
      "Croire que la rénovation dispense du CREP (faux)",
      "Confondre CREP vente (illimité) et CREP location (6 ans)",
      "Ne pas traiter les revêtements dégradés contenant du plomb",
      "Oublier les parties communes dans une copropriété avant 1949",
    ],
  },

  electricite: {
    slug: "electricite",
    name: "Électricité",
    fullName: "Diagnostic Électricité",
    icon: "ri-flashlight-line",
    heroTitle: "Diagnostic Électricité à Paris & Île-de-France",
    heroSubtitle: "Obligatoire pour les installations de plus de 15 ans — Certifié COFRAC, dès 100 €",
    price: "Dès 100 €",
    priceDetail: "TTC, déplacement inclus en IDF",
    metaTitle: "Diagnostic Électricité Paris & IDF — Certifié COFRAC, Dès 100 € | Up Diag",
    metaDesc: "Diagnostic électricité certifié COFRAC à Paris et en IDF. Obligatoire pour installations +15 ans. Rapport le jour même, dès 100 €.",
    heroImage: "/img/readdy/elec-hero.jpg",
    whatIs: "Le diagnostic électricité évalue l'état de l'installation électrique intérieure d'un logement. Il vérifie la conformité aux normes de sécurité en vigueur (NF C 16-600) et identifie les anomalies pouvant présenter un risque pour les occupants.",
    whatIsDetail: "Obligatoire pour la vente des logements dont l'installation électrique a plus de 15 ans, le diagnostic électricité est également requis pour la location depuis 2018. À Paris, de nombreux immeubles haussmanniens ont des installations vieillissantes nécessitant ce diagnostic.",
    obligations: "Le diagnostic électricité est obligatoire dans les cas suivants :",
    obligationItems: [
      "Vente d'un logement avec installation électrique de plus de 15 ans",
      "Location d'un logement avec installation électrique de plus de 15 ans (depuis 2018)",
      "Logements dont la dernière mise en conformité date de plus de 15 ans",
    ],
    steps: [
      { title: "Prise de rendez-vous", desc: "Devis gratuit, intervention sous 48h dans toute l'IDF.", icon: "ri-calendar-check-line" },
      { title: "Inspection du tableau électrique", desc: "Vérification du tableau de répartition, des disjoncteurs, des fusibles et des dispositifs de protection différentielle.", icon: "ri-search-eye-line" },
      { title: "Contrôle des circuits", desc: "Vérification des prises, interrupteurs, liaisons équipotentielles dans les pièces humides, mise à la terre.", icon: "ri-plug-line" },
      { title: "Rapport certifié", desc: "Rapport détaillé avec liste des anomalies classées par niveau de risque. Envoi le jour même.", icon: "ri-file-check-line" },
    ],
    checklist: [
      "Accès au tableau électrique principal",
      "Accès à toutes les pièces du logement",
      "Accès aux sous-sols et combles si applicable",
      "Informations sur la date de la dernière mise aux normes",
    ],
    prices: [
      { label: "Studio / T1 (< 35 m²)", price: "100 €", highlight: true },
      { label: "T2 / T3 (35–80 m²)", price: "120 €", highlight: true },
      { label: "T4+ / Maison (> 80 m²)", price: "140 €", highlight: true },
    ],
    faq: [
      { q: "Mon installation a été refaite il y a 10 ans, ai-je besoin du diagnostic ?", a: "Non. Si votre installation électrique a moins de 15 ans, le diagnostic n'est pas obligatoire. Conservez les justificatifs des travaux." },
      { q: "Quelle est la durée de validité du diagnostic électricité ?", a: "3 ans pour la vente, 6 ans pour la location." },
      { q: "Le diagnostic électricité impose-t-il des travaux ?", a: "Non, le diagnostic est informatif. Il identifie les anomalies mais n'impose pas de travaux obligatoires pour la vente. Cependant, les anomalies graves doivent être signalées à l'acheteur." },
      { q: "Que vérifie exactement le diagnostiqueur ?", a: "Il vérifie le tableau électrique, les dispositifs de protection, la mise à la terre, les liaisons équipotentielles dans les salles de bain, l'absence de matériels vétustes ou dangereux." },
    ],
    related: ["pack-vente", "pack-location", "gaz"],
    errors: [
      "Croire que le diagnostic impose des travaux obligatoires (informatif uniquement)",
      "Ne pas fournir l'accès au tableau électrique",
      "Confondre diagnostic électricité et mise aux normes",
      "Oublier de renouveler le diagnostic expiré avant la vente",
    ],
  },

  gaz: {
    slug: "gaz",
    name: "Gaz",
    fullName: "Diagnostic Gaz",
    icon: "ri-gas-station-line",
    heroTitle: "Diagnostic Gaz à Paris & Île-de-France",
    heroSubtitle: "Obligatoire pour les installations de plus de 15 ans — Certifié COFRAC, dès 100 €",
    price: "Dès 100 €",
    priceDetail: "TTC, déplacement inclus en IDF",
    metaTitle: "Diagnostic Gaz Paris & IDF — Certifié COFRAC, Dès 100 € | Up Diag",
    metaDesc: "Diagnostic gaz certifié COFRAC à Paris et en IDF. Obligatoire pour installations +15 ans. Rapport le jour même, dès 100 €. RDV sous 48h.",
    heroImage: "/img/readdy/gaz-hero.jpg",
    whatIs: "Le diagnostic gaz évalue l'état de l'installation intérieure de gaz d'un logement. Il vérifie la sécurité des équipements (chaudière, cuisinière, chauffe-eau) et des tuyauteries pour prévenir les risques d'explosion et d'intoxication au monoxyde de carbone.",
    whatIsDetail: "Obligatoire pour la vente et la location des logements dont l'installation gaz a plus de 15 ans, ce diagnostic est particulièrement important à Paris où de nombreux immeubles anciens disposent d'installations vieillissantes.",
    obligations: "Le diagnostic gaz est obligatoire dans les cas suivants :",
    obligationItems: [
      "Vente d'un logement avec installation gaz de plus de 15 ans",
      "Location d'un logement avec installation gaz de plus de 15 ans (depuis 2018)",
      "Logements dont la dernière mise en conformité date de plus de 15 ans",
    ],
    steps: [
      { title: "Prise de rendez-vous", desc: "Devis gratuit, intervention sous 48h dans toute l'IDF.", icon: "ri-calendar-check-line" },
      { title: "Inspection des équipements", desc: "Vérification de la chaudière, du chauffe-eau, de la cuisinière et de tous les appareils à gaz.", icon: "ri-search-eye-line" },
      { title: "Contrôle des tuyauteries", desc: "Inspection des tuyaux, robinets, raccords et détection de fuites éventuelles.", icon: "ri-tools-line" },
      { title: "Rapport certifié", desc: "Rapport avec classification des anomalies (A1, A2, DGI). Envoi le jour même.", icon: "ri-file-check-line" },
    ],
    checklist: [
      "Accès à tous les appareils à gaz (chaudière, cuisinière, chauffe-eau)",
      "Accès au compteur gaz",
      "Informations sur la date d'installation des équipements",
      "Accès aux gaines de ventilation",
    ],
    prices: [
      { label: "Studio / T1 (< 35 m²)", price: "100 €", highlight: true },
      { label: "T2 / T3 (35–80 m²)", price: "120 €", highlight: true },
      { label: "T4+ / Maison (> 80 m²)", price: "140 €", highlight: true },
    ],
    faq: [
      { q: "Que signifient les anomalies A1, A2 et DGI ?", a: "A1 = anomalie sans danger immédiat (recommandation). A2 = anomalie présentant un risque (travaux conseillés). DGI = Danger Grave et Immédiat (coupure obligatoire du gaz)." },
      { q: "Quelle est la durée de validité du diagnostic gaz ?", a: "3 ans pour la vente, 6 ans pour la location." },
      { q: "Que se passe-t-il en cas de DGI (Danger Grave et Immédiat) ?", a: "En cas de DGI, le diagnostiqueur est tenu d'informer immédiatement le distributeur de gaz qui procède à la coupure. Des travaux sont obligatoires avant toute remise en service." },
      { q: "Mon logement n'a pas de gaz, ai-je besoin de ce diagnostic ?", a: "Non. Le diagnostic gaz n'est obligatoire que si le logement dispose d'une installation intérieure de gaz." },
    ],
    related: ["pack-vente", "pack-location", "electricite"],
    errors: [
      "Ignorer une anomalie A2 lors d'une vente",
      "Ne pas couper le gaz en cas de DGI détecté",
      "Oublier de vérifier la ventilation des appareils à gaz",
      "Confondre diagnostic gaz et entretien chaudière",
    ],
  },

  termites: {
    slug: "termites",
    name: "Termites",
    fullName: "Diagnostic Termites",
    icon: "ri-bug-line",
    heroTitle: "Diagnostic Termites à Paris & Île-de-France",
    heroSubtitle: "Obligatoire dans les zones à risque définies par arrêté préfectoral — Dès 90 €",
    price: "Dès 90 €",
    priceDetail: "TTC, déplacement inclus en IDF",
    metaTitle: "Diagnostic Termites Paris & IDF — Zones à Risque, Dès 90 € | Up Diag",
    metaDesc: "Diagnostic termites à Paris et en IDF. Obligatoire dans les zones à risque. Inspection complète, rapport certifié, dès 90 €. RDV sous 48h.",
    heroImage: "/img/readdy/term-hero.jpg",
    whatIs: "Le diagnostic termites recherche la présence de termites et autres insectes xylophages (qui se nourrissent de bois) dans un bien immobilier. Les termites peuvent causer des dégâts structurels importants en s'attaquant aux charpentes, planchers et menuiseries.",
    whatIsDetail: "Le diagnostic termites est obligatoire dans les zones définies par arrêté préfectoral comme étant à risque. En Île-de-France, plusieurs communes sont concernées. La liste est mise à jour régulièrement par les préfectures.",
    obligations: "Le diagnostic termites est obligatoire dans les cas suivants :",
    obligationItems: [
      "Vente d'un bien situé dans une zone à risque termites",
      "Zone définie par arrêté préfectoral (liste consultable en mairie)",
      "Plusieurs communes d'Île-de-France sont concernées",
      "Validité de 6 mois seulement (à renouveler si expirée)",
    ],
    steps: [
      { title: "Vérification de la zone", desc: "Nous vérifions si votre commune est classée en zone à risque termites selon les arrêtés préfectoraux en vigueur.", icon: "ri-map-pin-2-line" },
      { title: "Inspection visuelle complète", desc: "Examen minutieux de toutes les parties en bois accessibles : charpente, planchers, menuiseries, parquets, plinthes.", icon: "ri-search-eye-line" },
      { title: "Sondage des bois", desc: "Sondage des éléments en bois avec un poinçon pour détecter les galeries creusées par les termites.", icon: "ri-tools-line" },
      { title: "Rapport certifié", desc: "Rapport avec localisation des zones infestées ou suspectes. Envoi le jour même.", icon: "ri-file-check-line" },
    ],
    checklist: [
      "Accès à la charpente et aux combles",
      "Accès aux sous-sols et vides sanitaires",
      "Accès à toutes les pièces avec parquet ou plancher bois",
      "Informations sur les traitements anti-termites déjà réalisés",
    ],
    prices: [
      { label: "Appartement (toute surface)", price: "90 €", highlight: true },
      { label: "Maison individuelle", price: "120 €", highlight: true },
      { label: "Maison avec combles accessibles", price: "150 €", highlight: false },
    ],
    faq: [
      { q: "Comment savoir si ma commune est en zone à risque termites ?", a: "Vous pouvez consulter la liste des communes concernées en mairie ou sur le site de la préfecture. Nous pouvons également vérifier pour vous lors de votre demande de devis." },
      { q: "Quelle est la durée de validité du diagnostic termites ?", a: "Le diagnostic termites n'est valable que 6 mois. Si votre bien n'est pas vendu dans ce délai, il faudra le renouveler." },
      { q: "Que faire si des termites sont détectés ?", a: "En cas de présence de termites, le propriétaire doit déclarer l'infestation en mairie dans le mois suivant la découverte. Un traitement par une entreprise spécialisée est nécessaire." },
      { q: "Le diagnostic termites est-il obligatoire pour la location ?", a: "Non, le diagnostic termites n'est pas obligatoire pour la location, uniquement pour la vente dans les zones à risque." },
    ],
    related: ["pack-vente", "amiante", "dpe"],
    errors: [
      "Ne pas vérifier si la commune est en zone à risque avant la vente",
      "Utiliser un diagnostic termites de plus de 6 mois",
      "Ne pas déclarer une infestation en mairie",
      "Confondre diagnostic termites et traitement anti-termites",
    ],
  },

  "loi-carrez": {
    slug: "loi-carrez",
    name: "Loi Carrez",
    fullName: "Mesurage Loi Carrez",
    icon: "ri-ruler-2-line",
    heroTitle: "Mesurage Loi Carrez à Paris & Île-de-France",
    heroSubtitle: "Obligatoire pour la vente de tout lot en copropriété — Certifié, dès 80 €",
    price: "Dès 80 €",
    priceDetail: "TTC, déplacement inclus en IDF",
    metaTitle: "Mesurage Loi Carrez Paris & IDF — Certifié, Dès 80 € | Up Diag",
    metaDesc: "Mesurage Loi Carrez certifié à Paris et en IDF. Obligatoire pour vendre en copropriété. Précision millimétrique, rapport le jour même, dès 80 €.",
    heroImage: "/img/readdy/carrez-hero.jpg",
    whatIs: "Le mesurage Loi Carrez détermine la superficie privative d'un lot de copropriété. Cette surface, exprimée en m², doit obligatoirement figurer dans tout acte de vente d'un lot en copropriété. Elle exclut les surfaces dont la hauteur sous plafond est inférieure à 1,80 m.",
    whatIsDetail: "La Loi Carrez du 18 décembre 1996 protège les acheteurs en copropriété contre les erreurs de surface. Si la surface réelle est inférieure de plus de 5% à la surface mentionnée dans l'acte, l'acheteur peut demander une réduction du prix proportionnelle.",
    obligations: "Le mesurage Loi Carrez est obligatoire dans les cas suivants :",
    obligationItems: [
      "Vente de tout lot ou fraction de lot en copropriété",
      "Appartements, maisons en copropriété, parkings, caves (si > 8 m²)",
      "Mention obligatoire dans la promesse de vente et l'acte authentique",
      "Validité illimitée si aucun travaux modifiant la surface n'a été réalisé",
    ],
    steps: [
      { title: "Prise de rendez-vous", desc: "Devis gratuit, intervention sous 48h dans toute l'IDF.", icon: "ri-calendar-check-line" },
      { title: "Mesurage au laser", desc: "Mesure précise de chaque pièce avec un télémètre laser. Prise en compte des règles Carrez (hauteur > 1,80 m).", icon: "ri-ruler-2-line" },
      { title: "Calcul de la surface", desc: "Calcul de la surface privative selon la méthode Carrez : déduction des murs, cloisons, marches, gaines, embrasures.", icon: "ri-calculator-line" },
      { title: "Rapport certifié", desc: "Attestation de surface Loi Carrez avec plan coté. Envoi le jour même, valable pour votre notaire.", icon: "ri-file-check-line" },
    ],
    checklist: [
      "Accès à toutes les pièces du lot",
      "Accès aux annexes (cave, parking, grenier si inclus dans le lot)",
      "Règlement de copropriété si disponible",
      "Plans existants si disponibles (pour vérification)",
    ],
    prices: [
      { label: "Studio / T1 (< 35 m²)", price: "80 €", highlight: true },
      { label: "T2 / T3 (35–80 m²)", price: "100 €", highlight: true },
      { label: "T4+ (> 80 m²)", price: "120 €", highlight: true },
      { label: "Avec plan coté inclus", price: "+20 €", highlight: false },
    ],
    faq: [
      { q: "La surface Carrez est-elle la même que la surface habitable ?", a: "Non. La surface Carrez (copropriété) et la surface habitable (loi Boutin, pour la location) sont calculées différemment. La surface Carrez inclut les placards et dressings, pas la surface habitable." },
      { q: "Quelle est la durée de validité du mesurage Carrez ?", a: "Le mesurage Carrez a une durée illimitée, sauf si des travaux modifiant la surface ont été réalisés (abattage de cloisons, extension, etc.)." },
      { q: "Que se passe-t-il si la surface réelle est inférieure à celle indiquée ?", a: "Si l'écart est supérieur à 5%, l'acheteur peut demander une réduction du prix proportionnelle à l'écart dans un délai d'un an après la signature de l'acte." },
      { q: "Le mesurage Carrez est-il obligatoire pour une maison individuelle ?", a: "Non. Le mesurage Carrez ne s'applique qu'aux lots en copropriété. Pour une maison individuelle hors copropriété, il n'est pas obligatoire." },
    ],
    related: ["pack-vente", "dpe", "electricite"],
    errors: [
      "Confondre surface Carrez et surface habitable (loi Boutin)",
      "Oublier de déduire les surfaces sous 1,80 m de hauteur",
      "Ne pas renouveler le mesurage après des travaux modifiant la surface",
      "Omettre la surface Carrez dans la promesse de vente",
    ],
  },
};
